import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spin, Table } from "antd";

import api from "../../api/api";
import AdminHeader from "../../components/AdminHeader";
import { useLinkProfile } from "../../api/useLinkProfile";
import { useSubscription } from "../../api/useSubscription";
import { LinkProfile } from "../../models";
import { formatPlanPrice } from "../../utils/planUtils";

interface Stats {
  total_link_clicks: number;
  total_links: number;
  last_7_days: number;
  mtd: number;
  last_month: number;
  ytd: number;
}

enum statsCategory {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    total_link_clicks: 0,
    total_links: 0,
    last_7_days: 0,
    mtd: 0,
    last_month: 0,
    ytd: 0,
  });
  const [loading, setLoading] = useState(true);

  const [statsDetial, setStatsDetail] = useState<
    {
      date: string;
      clicks: number;
    }[]
  >([]);
  const [category, setCategory] = useState<statsCategory>(statsCategory.DAY);

  const {
    loading: linkProfileLoading,
    linkProfiles,
    linkProfileStats,
    linkProfileStatsDetail,
  } = useLinkProfile();

  const { subscription, getSubscription } = useSubscription();

  useEffect(() => {
    getSubscription().catch(() => {
      // Plan summary is optional on the dashboard.
    });
  }, [getSubscription]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Replace with your actual API endpoint
        const response = await api.get("/v1/profile/");
        setStats(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load stats", err);
        setLoading(false);
      }
    };

    fetchStats();
    linkProfileStats()
      .then((data) => {
        setStats((prevStats) => ({
          ...prevStats,
          total_link_clicks: data.total,
          last_7_days: data.last_7_days,
          mtd: data.mtd,
          last_month: data.last_month,
          ytd: data.ytd,
        }));
      })
      .catch((err) => {
        console.error("Failed to load link profile stats", err);
      });

    linkProfileStatsDetail(category)
      .then((data: Record<string, number>) => {
        // Handle the detailed stats data if needed
        setStatsDetail(
          Object.entries(data).map(([date, clicks]) => ({
            date,
            clicks,
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to load link profile stats detail", err);
      });
  }, []);

  // Link profile table columns
  const columns = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string, record: LinkProfile) =>
        path === "" ? "(Main link)" : path,
    },
    {
      title: "Clicks",
      dataIndex: "link_counter",
      key: "link_counter",
      sorter: (a: LinkProfile, b: LinkProfile) =>
        (a.link_counter || 0) - (b.link_counter || 0),
    },
  ];

  const DetailsColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      key: "clicks",
    },
  ];

  return (
    <AdminHeader>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-start gap-4 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            {subscription?.plan && (
              <Link to="/admin/subscription">
                <Button>
                  {subscription.plan.name} · {formatPlanPrice(subscription.plan)}
                </Button>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center mt-4">
              <Spin size="large" />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Clicks
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.total_link_clicks}
                  </dd>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Links
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.total_links}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-3 sm:p-4 text-center">
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    Last 7 Days
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-gray-900">
                    {stats.last_7_days}
                  </dd>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-3 sm:p-4 text-center">
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    MTD
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-gray-900">
                    {stats.mtd}
                  </dd>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-3 sm:p-4 text-center">
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    Last Month
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-gray-900">
                    {stats.last_month}
                  </dd>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-3 sm:p-4 text-center">
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    YTD
                  </dt>
                  <dd className="mt-1 text-xl font-semibold text-gray-900">
                    {stats.ytd}
                  </dd>
                </div>
              </div>
            </div>
          }

          <div className="mt-8 w-full">
            <div className="flex flex-row gap-8">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Link Performance
                </h2>
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <Table
                    dataSource={linkProfiles}
                    columns={columns}
                    rowKey="id"
                    showSorterTooltip={false}
                    pagination={{ pageSize: 10 }}
                    loading={linkProfileLoading}
                  />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Performance By Date
                </h2>
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <Table
                    dataSource={statsDetial}
                    columns={DetailsColumns}
                    rowKey="date"
                    showSorterTooltip={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminHeader>
  );
};

export default Dashboard;
